/**
 * The equivalent location in another app: the target app's root under the customer the given
 * location is already scoped to.
 *
 * **Nothing below the app segment carries across.** A deep path is defined by the app that defined
 * it, so `patients/:patientId` means nothing in an app with no patients table; translating one
 * would require knowing another app's route table. A switch therefore always lands on the target
 * app's root, which is the only location every app is guaranteed to have.
 *
 * The customer is read from `pathname` rather than taken as an argument, so a caller cannot pass a
 * customer that disagrees with the URL the user is on. A pathname that is not customer-scoped (the
 * bootstrap index, `/unauthorized`, `/launch`) has no customer to preserve, so the result is the
 * app-less root and the bootstrap resolves a customer again.
 */
export const switchApp = (pathname: string, appId: string): string => {
  const customer = customerOf(pathname)

  return customer === undefined ? '/' : `/c/${customer}/${appId}`
}

/**
 * The customer a pathname is scoped to, or `undefined` when it is not customer-scoped.
 *
 * Matches the `c/:customer` mount point in the shell's route table: the `/c/` prefix is what keeps
 * an app-global route from ever being read as a customer id (Atlas ADR-0001).
 */
const customerOf = (pathname: string): string | undefined => {
  const [, prefix, customer] = pathname.split('/')

  return prefix === 'c' && customer !== undefined && customer !== ''
    ? customer
    : undefined
}
